import { useCallback, useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import VehicleFormModal from "@/pages/admin/customers/components/VehicleFormModal";
import { CUSTOMER_TYPE_LABELS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Customer, CustomerInvoiceSummary, CustomerSaleSummary, CustomerVehicle } from "@/types";

interface CustomerDetailModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer | null;
  onChanged: () => void;
}

const currency = new Intl.NumberFormat("es-DO", {
  style: "currency",
  currency: "DOP",
  minimumFractionDigits: 2,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function CustomerDetailModal({
  open,
  onClose,
  customer,
  onChanged,
}: CustomerDetailModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [tab, setTab] = useState<"info" | "vehicles" | "history">("info");
  const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
  const [sales, setSales] = useState<CustomerSaleSummary[]>([]);
  const [invoices, setInvoices] = useState<CustomerInvoiceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<CustomerVehicle | null>(null);

  const load = useCallback(async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const [vehiclesRes, salesRes, invoicesRes] = await Promise.all([
        supabase
          .from("customer_vehicles")
          .select("id, customer_id, brand, model, year, plate, vin, color, vehicle_type, mileage, notes, created_at")
          .eq("customer_id", customer.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("sales")
          .select("id, total, status, created_at")
          .eq("customer_id", customer.id)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("invoices")
          .select("id, number, total, status, created_at")
          .eq("customer_id", customer.id)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);
      setVehicles((vehiclesRes.data ?? []) as CustomerVehicle[]);
      setSales((salesRes.data ?? []) as CustomerSaleSummary[]);
      setInvoices((invoicesRes.data ?? []) as CustomerInvoiceSummary[]);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    if (!open || !customer) return;
    setTab("info");
    load();
  }, [open, customer, load]);

  const handleConvert = async () => {
    if (!customer) return;
    const target = customer.customer_type === "mayorista" ? "regular" : "mayorista";
    setConverting(true);
    const { error } = await supabase
      .from("customers")
      .update({ customer_type: target, updated_at: new Date().toISOString() })
      .eq("id", customer.id);
    setConverting(false);
    if (error) {
      toast.error("No se pudo cambiar el tipo de cliente.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "editar",
      entity: "customers",
      entityId: customer.id,
      branchId: profile?.branch_id ?? null,
      oldValue: { tipo: customer.customer_type },
      newValue: { tipo: target },
    });
    toast.success(
      target === "mayorista"
        ? "Cliente convertido a mayorista. Ahora paga precio mayorista."
        : "Cliente convertido a regular.",
    );
    onChanged();
  };

  const handleDeleteVehicle = async (vehicle: CustomerVehicle) => {
    const { error } = await supabase.from("customer_vehicles").delete().eq("id", vehicle.id);
    if (error) {
      toast.error("No se pudo eliminar el vehículo.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "eliminar",
      entity: "customer_vehicles",
      entityId: vehicle.id,
      branchId: profile?.branch_id ?? null,
    });
    toast.success("Vehículo eliminado.");
    load();
  };

  if (!customer) return null;

  const isWholesale = customer.customer_type === "mayorista";
  const tabs = [
    { key: "info", label: "Información", icon: "ri-user-3-line" },
    { key: "vehicles", label: `Vehículos (${vehicles.length})`, icon: "ri-truck-line" },
    { key: "history", label: "Historial", icon: "ri-history-line" },
  ] as const;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        title={`${customer.first_name} ${customer.last_name}`.trim()}
        subtitle={
          isWholesale
            ? "Cliente mayorista · precio mayorista automático en el POS"
            : "Cliente regular · precio de venta normal"
        }
        footer={
          <>
            <button
              type="button"
              onClick={handleConvert}
              disabled={converting}
              className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
                isWholesale
                  ? "border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100"
                  : "border-accent-300 bg-accent-50 text-accent-900 hover:bg-accent-100"
              }`}
            >
              {converting ? (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              ) : (
                <i className={`${isWholesale ? "ri-arrow-down-line" : "ri-arrow-up-line"} text-base`}></i>
              )}
              {isWholesale ? "Convertir a regular" : "Convertir a mayorista"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              Cerrar
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold transition-colors ${
                  tab === item.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${item.icon} text-sm`}></i>
                {item.label}
              </button>
            ))}
          </div>

          {tab === "info" ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    isWholesale ? "bg-accent-100 text-accent-900" : "bg-secondary-100 text-secondary-900"
                  }`}
                >
                  {CUSTOMER_TYPE_LABELS[customer.customer_type] ?? customer.customer_type}
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    customer.active ? "bg-background-200 text-foreground-700" : "bg-primary-100 text-primary-700"
                  }`}
                >
                  {customer.active ? "Activo" : "Inactivo"}
                </span>
              </div>

              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  { label: "Documento", value: customer.document, icon: "ri-id-card-line" },
                  { label: "Teléfono", value: customer.phone, icon: "ri-phone-line" },
                  { label: "WhatsApp", value: customer.whatsapp, icon: "ri-whatsapp-line" },
                  { label: "Correo", value: customer.email, icon: "ri-mail-line" },
                  { label: "Dirección", value: customer.address, icon: "ri-map-pin-line" },
                  { label: "Cliente desde", value: customer.created_at ? formatDate(customer.created_at) : null, icon: "ri-calendar-line" },
                ].map((item) => (
                  <div key={item.label} className="rounded-md border border-background-200 bg-background-100 px-3.5 py-3">
                    <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
                      <i className={`${item.icon} text-sm`}></i>
                      {item.label}
                    </dt>
                    <dd className="mt-1 truncate text-sm text-foreground-900">{item.value || "—"}</dd>
                  </div>
                ))}
              </dl>

              {customer.notes ? (
                <div className="rounded-md border border-background-200 bg-background-100 px-3.5 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-400">Notas</p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-700">{customer.notes}</p>
                </div>
              ) : null}
            </div>
          ) : null}

          {tab === "vehicles" ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-foreground-500">
                  Vehículos registrados para este cliente.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setEditingVehicle(null);
                    setVehicleOpen(true);
                  }}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-add-line text-sm"></i>
                  Agregar
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-foreground-500">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Cargando...
                </div>
              ) : vehicles.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-10 text-center">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-truck-line text-xl"></i>
                  </span>
                  <p className="text-sm font-medium text-foreground-700">Sin vehículos registrados</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {vehicles.map((vehicle) => (
                    <li
                      key={vehicle.id}
                      className="flex flex-col gap-3 rounded-md border border-background-200 bg-background-100 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                          <i className="ri-truck-line text-base"></i>
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground-950">
                            {[vehicle.brand, vehicle.model].filter(Boolean).join(" ") || "Vehículo"}
                          </p>
                          <p className="truncate text-[11px] text-foreground-500">
                            {vehicle.plate ? `Placa ${vehicle.plate}` : "Sin placa"}
                            {vehicle.year ? ` · ${vehicle.year}` : ""}
                            {vehicle.mileage != null ? ` · ${vehicle.mileage} km` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingVehicle(vehicle);
                            setVehicleOpen(true);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-edit-2-line text-sm"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteVehicle(vehicle)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-primary-200 bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100"
                        >
                          <i className="ri-delete-bin-6-line text-sm"></i>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {tab === "history" ? (
            <div className="flex flex-col gap-4">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  Facturas ({invoices.length})
                </p>
                {invoices.length === 0 ? (
                  <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-3.5 py-4 text-center text-xs text-foreground-500">
                    Sin facturas registradas todavía.
                  </p>
                ) : (
                  <ul className="flex flex-col divide-y divide-background-200 rounded-md border border-background-200 bg-background-50">
                    {invoices.map((invoice) => (
                      <li key={invoice.id} className="flex items-center justify-between gap-3 px-3.5 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground-900">
                            {invoice.number || "Factura"}
                          </p>
                          <p className="text-[11px] text-foreground-400">{formatDate(invoice.created_at)}</p>
                        </div>
                        <span className="font-heading text-sm font-bold text-foreground-950">
                          {currency.format(Number(invoice.total ?? 0))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  Ventas ({sales.length})
                </p>
                {sales.length === 0 ? (
                  <p className="rounded-md border border-dashed border-background-300 bg-background-100 px-3.5 py-4 text-center text-xs text-foreground-500">
                    Sin ventas registradas todavía.
                  </p>
                ) : (
                  <ul className="flex flex-col divide-y divide-background-200 rounded-md border border-background-200 bg-background-50">
                    {sales.map((sale) => (
                      <li key={sale.id} className="flex items-center justify-between gap-3 px-3.5 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground-900">
                            {sale.status || "Venta"}
                          </p>
                          <p className="text-[11px] text-foreground-400">{formatDate(sale.created_at)}</p>
                        </div>
                        <span className="font-heading text-sm font-bold text-foreground-950">
                          {currency.format(Number(sale.total ?? 0))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </Modal>

      <VehicleFormModal
        open={vehicleOpen}
        onClose={() => setVehicleOpen(false)}
        onSaved={() => {
          load();
          onChanged();
        }}
        vehicle={editingVehicle}
        customerId={customer.id}
        customers={[]}
      />
    </>
  );
}